import { useEffect, useMemo, useState } from "react";
import { getGPUTier } from "detect-gpu";
import { prefersReducedMotion } from "@/lib/gsapSetup";
import { heroConfig } from "../config/heroConfig";

function tierFromGpu(result) {
  const fps = result?.fps ?? 0;
  const { highMinFps, mediumMinFps } = heroConfig.performance.gpu;
  if (result?.isMobile && fps < highMinFps) {
    return fps >= mediumMinFps ? "medium" : "low";
  }
  if (fps >= highMinFps) return "high";
  if (fps >= mediumMinFps) return "medium";
  return "low";
}

const TIER_RANK = { low: 0, medium: 1, high: 2 };

/**
 * GPU detection + reduced-motion → render budget for the Hero Canvas.
 * Safe defaults until detect-gpu resolves (medium, shadows on desktop-ish).
 */
export default function usePerformanceMode() {
  const reducedMotion = useMemo(() => prefersReducedMotion(), []);
  const [gpuTier, setGpuTier] = useState(null);
  const [tier, setTier] = useState(reducedMotion ? "low" : "medium");

  useEffect(() => {
    let cancelled = false;

    if (reducedMotion) {
      setTier("low");
      return undefined;
    }

    (async () => {
      try {
        const result = await getGPUTier({ benchmarksURL: undefined });
        if (cancelled) return;
        setGpuTier(result);
        setTier(tierFromGpu(result));
      } catch {
        if (!cancelled) setTier("medium");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reducedMotion]);

  const dpr = heroConfig.performance.dpr[tier] ?? heroConfig.performance.dpr.medium;
  const shadowFloor = heroConfig.performance.enableShadowsAboveTier;
  const enableShadows =
    !reducedMotion &&
    TIER_RANK[tier] >= TIER_RANK[shadowFloor] &&
    heroConfig.renderer.shadows;

  /** Skip WebGL entirely for reduced motion or known very weak GPUs */
  const enableScene = !reducedMotion && tier !== "low";

  return {
    reducedMotion,
    tier,
    gpuTier,
    dpr,
    enableShadows,
    enableScene,
    antialias: tier === "high",
    powerPreference: heroConfig.renderer.powerPreference,
  };
}
